import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';

function FAQPage() {
  const [openFAQ, setOpenFAQ] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const faqData = [
    {
      id: 1,
      category: 'General',
      question: 'What is Vencejo Academy and how does it work?',
      answer: 'Vencejo Academy is a comprehensive digital Spanish learning platform designed for English speakers. We offer interactive courses, music-based learning, cultural content, and personalized tutoring. Our products are instantly downloadable and work on all devices.'
    },
    {
      id: 2,
      category: 'Products',
      question: 'What\'s included in the Complete Spanish Learning Bundle?',
      answer: 'The Complete Bundle includes 50+ interactive video lessons, cultural context materials, music analysis with 100+ songs, grammar exercises, pronunciation training, mobile app access, lifetime updates, and a certificate of completion. It\'s our most comprehensive package.'
    },
    {
      id: 3,
      category: 'Technical',
      question: 'How do I download my products after purchase?',
      answer: 'After completing your purchase, you\'ll receive an email with download links within 5 minutes. You can also access your products from your account dashboard. All products are available for immediate download and work offline.'
    },
    {
      id: 4,
      category: 'Billing',
      question: 'Do you offer refunds?',
      answer: 'Yes! We offer a 30-day money-back guarantee on all products. If you\'re not satisfied for any reason, contact our support team within 30 days of purchase for a full refund, no questions asked.'
    },
    {
      id: 5,
      category: 'Learning',
      question: 'What Spanish level do I need to start?',
      answer: 'Our products cater to all levels from complete beginner (A1) to advanced (C2). Each product clearly indicates its target level. Beginners can start with our Travel Spanish Essentials or Complete Bundle, which includes beginner-friendly content.'
    },
    {
      id: 6,
      category: 'Technical',
      question: 'What devices are supported?',
      answer: 'All our digital products work on Windows, Mac, iOS, and Android devices. You can access content on computers, tablets, and smartphones. Some products include dedicated mobile apps for enhanced learning on the go.'
    },
    {
      id: 7,
      category: 'Products',
      question: 'How is learning through music different?',
      answer: 'Our music-based learning uses authentic Spanish songs with translations, cultural context, and vocabulary exercises. Music helps with pronunciation, rhythm, and memorization. It makes learning enjoyable and helps you understand real Spanish as spoken by natives.'
    },
    {
      id: 8,
      category: 'Billing',
      question: 'Are there any subscription fees?',
      answer: 'No! All our products are one-time purchases with lifetime access. You pay once and own the content forever, including all future updates. There are no monthly fees or hidden costs.'
    },
    {
      id: 9,
      category: 'Learning',
      question: 'How long does it take to see results?',
      answer: 'Results vary by individual and time invested, but most students report noticeable improvement within 2-4 weeks of consistent study. Our structured approach and engaging content help accelerate the learning process compared to traditional methods.'
    },
    {
      id: 10,
      category: 'Technical',
      question: 'Can I access my content offline?',
      answer: 'Yes! Once downloaded, most content can be accessed offline. Audio lessons, PDFs, and videos work without internet connection. Some interactive features may require internet for the best experience.'
    },
    {
      id: 11,
      category: 'Products',
      question: 'What makes your Business Spanish course special?',
      answer: 'Our Business Spanish Mastery course includes industry-specific vocabulary, formal communication patterns, cultural business etiquette, email templates, presentation skills, and real-world scenarios. It\'s designed for professionals who need Spanish for career advancement.'
    },
    {
      id: 12,
      category: 'General',
      question: 'Do you offer customer support?',
      answer: 'Absolutely! We provide email support, live chat (Mon-Fri 9AM-6PM EST), and phone support. Our average response time is under 2 hours for technical issues and under 24 hours for general inquiries.'
    },
    {
      id: 13,
      category: 'Learning',
      question: 'Can children use these products?',
      answer: 'Yes! We have a dedicated Kids Spanish Adventure product designed for children ages 5-12. It includes games, animated stories, songs, and age-appropriate content. Adult supervision is recommended for younger children.'
    },
    {
      id: 14,
      category: 'Technical',
      question: 'What if I lose my download files?',
      answer: 'No problem! You can re-download your products anytime from your account dashboard. We keep your purchase history permanently, so you\'ll never lose access to your content.'
    },
    {
      id: 15,
      category: 'Products',
      question: 'Are your courses created by native speakers?',
      answer: 'Yes! All our content is created and reviewed by native Spanish speakers and certified language instructors. We ensure authenticity, proper pronunciation, and cultural accuracy in all our materials.'
    }
  ];

  const categories = ['All', 'General', 'Products', 'Technical', 'Billing', 'Learning'];

  const [selectedCategory, setSelectedCategory] = useState('All');

  const filteredFAQs = faqData.filter(faq => {
    const matchesCategory = selectedCategory === 'All' || faq.category === selectedCategory;
    const matchesSearch = faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         faq.answer.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const toggleFAQ = (id) => {
    setOpenFAQ(openFAQ === id ? null : id);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            Frequently Asked Questions
          </h1>
          <p className="text-xl md:text-2xl mb-8 opacity-90">
            Find quick answers to common questions about our Spanish learning products
          </p>
          
          {/* Search Bar */}
          <div className="max-w-md mx-auto">
            <div className="relative">
              <input
                type="text"
                placeholder="Search FAQs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-3 pl-12 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-white"
              />
              <svg className="absolute left-4 top-3.5 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Stats */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-3xl font-bold text-blue-600 mb-2">15+</div>
              <div className="text-gray-600">Common Questions</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-green-600 mb-2">&lt; 2hrs</div>
              <div className="text-gray-600">Response Time</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-purple-600 mb-2">24/7</div>
              <div className="text-gray-600">Self-Service</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-orange-600 mb-2">100%</div>
              <div className="text-gray-600">Satisfaction</div>
            </div>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-4">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-3 rounded-full font-medium transition-all duration-300 ${
                  selectedCategory === category
                    ? 'bg-blue-600 text-white shadow-lg'
                    : 'bg-white text-gray-700 hover:bg-gray-50 shadow-md'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {filteredFAQs.length === 0 ? (
            <div className="text-center py-12">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                No FAQs found
              </h3>
              <p className="text-gray-600 mb-6">
                Try adjusting your search terms or browse different categories.
              </p>
              <button
                onClick={() => {
                  setSearchTerm('');
                  setSelectedCategory('All');
                }}
                className="bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition"
              >
                Show All FAQs
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredFAQs.map((faq) => (
                <div key={faq.id} className="bg-white border border-gray-200 rounded-lg shadow-sm">
                  <button
                    onClick={() => toggleFAQ(faq.id)}
                    className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center">
                      <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-medium mr-3">
                        {faq.category}
                      </span>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {faq.question}
                      </h3>
                    </div>
                    <svg
                      className={`w-5 h-5 text-gray-500 transition-transform ${
                        openFAQ === faq.id ? 'transform rotate-180' : ''
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {openFAQ === faq.id && (
                    <div className="px-6 pb-4">
                      <p className="text-gray-700 leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Still Need Help */}
      <section className="py-16 bg-blue-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Still Need Help?
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            Can't find what you're looking for? Our support team is here to help!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition"
            >
              Contact Support
            </Link>
            <Link
              to="/free-ebook"
              className="bg-white text-blue-600 border-2 border-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-blue-50 transition"
            >
              FREE EBOOK
            </Link>
          </div>
        </div>
      </section>

      {/* Shared Footer */}
      <Footer />
    </div>
  );
}

export default FAQPage;